"use client";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

interface StepItem {
  number: number;
  title: string;
  icon: string;
  description: string;
  proTip?: string;
  highlights?: string[];
  cta?: string;
  priceTag?: string;
}

const steps: StepItem[] = [
  {
    number: 1,
    title: "Create Your Free Account",
    icon: "🚀",
    description:
      "Click 'Get Started Free' and sign up with your email. Takes less than 30 seconds.",
    proTip:
      "Use your business email to establish credibility during verification.",
    cta: "Create Account Now →",
  },
  {
    number: 2,
    title: "Find & Claim Your Profile",
    icon: "🔍",
    description:
      "Search for your existing listing or create a new one. Click 'Claim This Listing' and provide your business details.",
    highlights: [
      "Business Name",
      "Email Address",
      "Specialty / Trade",
      "Phone Number",
    ],
    proTip:
      "Just provide company name, contact email, specialty (e.g. Venetian Plaster, Microcement), and phone. That's all we need to verify your claim!",
  },
  {
    number: 3,
    title: "Fast Approval (24–48 Hrs)",
    icon: "⏰",
    description:
      "Our team quickly verifies your business to maintain directory quality. We pride ourselves on fast approvals!",
    proTip:
      "Most profiles are approved within 24–48 hours so you can start attracting premium finish projects quickly.",
  },
  {
    number: 4,
    title: "Upgrade to Featured Listing",
    icon: "⭐",
    description:
      "Once approved, open your dashboard and click 'Upgrade to Featured'. Your profile jumps to the top of searches in your service areas.",
    proTip:
      "Instant benefits: 5x more visibility, priority placement, featured badge, higher inquiry rate.",
    priceTag: "$29/month — Cancel anytime",
  },
  {
    number: 5,
    title: "Add Projects & Optimize",
    icon: "📸",
    description:
      "Upload project photos, add service areas, list surface types (walls, bathrooms, floors) and finishes (Venetian, Tadelakt, Microcement).",
    highlights: [
      "Portfolio Shots",
      "Service Areas",
      "Finish Types",
      "Special Offers",
    ],
    proTip:
      "Profiles with 4+ high-quality images receive up to 2x more inquiries. Show close-up texture + full room context.",
  },
];

export function SetupGuideSection() {
  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-6 flex items-center justify-center gap-3">
            <span role="img" aria-label="clipboard">
              📋
            </span>
            <span>Your Complete Setup Guide</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Follow these simple steps to get featured and start receiving
            qualified plaster & microcement project inquiries. Most applicators
            finish in under 10 minutes.
          </p>
        </motion.div>

        <div className="space-y-10 max-w-5xl mx-auto">
          {steps.map((step, idx) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <Card className="p-6 md:p-8 bg-muted/40 dark:bg-muted border border-border/60">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex flex-col items-center md:items-start w-full md:w-48">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {step.number}
                    </div>
                    <div className="text-3xl" aria-hidden="true">
                      {step.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-semibold mb-3 text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {step.description}
                    </p>
                    {step.highlights && (
                      <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        {step.highlights.map((h) => (
                          <li
                            key={h}
                            className="text-xs md:text-sm px-2 py-1 rounded bg-background border border-border text-muted-foreground"
                          >
                            {h}
                          </li>
                        ))}
                      </ul>
                    )}
                    {step.proTip && (
                      <div className="text-xs md:text-sm italic text-primary/90 mb-4">
                        Pro Tip: {step.proTip}
                      </div>
                    )}
                    {step.priceTag && (
                      <div className="text-sm font-medium text-primary mb-4">
                        {step.priceTag}
                      </div>
                    )}
                    {step.cta && (
                      <Button variant="outline" size="sm">
                        {step.cta}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-20"
        >
          <p className="text-lg md:text-xl font-medium text-foreground mb-4">
            🎉 That&apos;s it! You&apos;re ready to grow.
          </p>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Most applicators receive their first qualified inquiry within 48
            hours of going live. Start building a steady pipeline of high-value
            finish projects.
          </p>
          <Button size="lg">Start Your Free Listing Now →</Button>
        </motion.div>
      </div>
    </section>
  );
}
